import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Bot } from "lucide-react";

export function AISettings() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [settings, setSettings] = useState({
    enable_ai_responses: false,
    ai_personality: '',
    fallback_message: '',
    max_conversation_turns: 5,
    ai_model: 'google/gemini-2.5-flash'
  });
  const [testMessage, setTestMessage] = useState('');
  const [testResponse, setTestResponse] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    const { data, error } = await supabase
      .from('ai_settings')
      .select('*')
      .limit(1)
      .maybeSingle();

    if (error) {
      toast({
        title: "Error",
        description: "Failed to load AI settings",
        variant: "destructive"
      });
      return;
    }

    if (data) {
      setSettings({
        enable_ai_responses: data.enable_ai_responses,
        ai_personality: data.ai_personality,
        fallback_message: data.fallback_message,
        max_conversation_turns: data.max_conversation_turns,
        ai_model: data.ai_model
      });
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('ai_settings')
        .upsert({
          ...settings,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "AI settings saved successfully"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!testMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter a test message",
        variant: "destructive"
      });
      return;
    }

    setTesting(true);
    setTestResponse('');

    try {
      // Create a test conversation
      const { data: conversation, error: convError } = await supabase
        .from('conversations')
        .insert({
          customer_phone: '+1234567890',
          customer_name: 'Test Customer',
          status: 'waiting'
        })
        .select()
        .single();

      if (convError) throw convError;

      // Create a test message
      const { data: message, error: msgError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_type: 'customer',
          content: testMessage
        })
        .select()
        .single();

      if (msgError) throw msgError;

      // Call AI function (note: this won't actually send SMS in test mode)
      const { data: aiData, error: aiError } = await supabase.functions.invoke('ai-respond', {
        body: {
          conversation_id: conversation.id,
          message_id: message.id,
          customer_phone: '+1234567890'
        }
      });

      if (aiError) throw aiError;

      // Fetch the AI response from messages
      const { data: aiMessage } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversation.id)
        .eq('sender_type', 'ai')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      setTestResponse(aiMessage?.content || aiData.message || 'No response received');

      // Cleanup test data
      await supabase.from('conversations').delete().eq('id', conversation.id);

      toast({
        title: "Test Complete",
        description: "AI response generated successfully"
      });
    } catch (error: any) {
      toast({
        title: "Test Failed",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5" />
            AI Configuration
          </CardTitle>
          <CardDescription>
            Configure AI-powered after-hours customer support
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable-ai">Enable AI Responses</Label>
              <p className="text-sm text-muted-foreground">
                AI will respond to customers after business hours
              </p>
            </div>
            <Switch
              id="enable-ai"
              checked={settings.enable_ai_responses}
              onCheckedChange={(checked) => 
                setSettings({ ...settings, enable_ai_responses: checked })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="personality">AI Personality</Label>
            <Textarea
              id="personality"
              placeholder="You are a friendly customer service AI..."
              value={settings.ai_personality}
              onChange={(e) => setSettings({ ...settings, ai_personality: e.target.value })}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {settings.ai_personality.length}/500 characters
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="fallback">Fallback Message</Label>
            <Textarea
              id="fallback"
              placeholder="I'd be happy to connect you with our team..."
              value={settings.fallback_message}
              onChange={(e) => setSettings({ ...settings, fallback_message: e.target.value })}
              rows={2}
              maxLength={160}
            />
            <p className="text-xs text-muted-foreground">
              {settings.fallback_message.length}/160 characters (SMS limit)
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="max-turns">Max Conversation Turns</Label>
            <Input
              id="max-turns"
              type="number"
              min={1}
              max={10}
              value={settings.max_conversation_turns}
              onChange={(e) => setSettings({ ...settings, max_conversation_turns: parseInt(e.target.value) })}
            />
            <p className="text-xs text-muted-foreground">
              Auto-escalate to human after this many AI responses
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="model">AI Model</Label>
            <Select
              value={settings.ai_model}
              onValueChange={(value) => setSettings({ ...settings, ai_model: value })}
            >
              <SelectTrigger id="model">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="google/gemini-2.5-flash">Gemini 2.5 Flash (Recommended)</SelectItem>
                <SelectItem value="google/gemini-2.5-flash-lite">Gemini 2.5 Flash Lite (Fastest)</SelectItem>
                <SelectItem value="google/gemini-2.5-pro">Gemini 2.5 Pro (Most Powerful)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              All Gemini models are currently FREE until Oct 6, 2025
            </p>
          </div>

          <Button onClick={handleSave} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save Settings
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Test AI Response</CardTitle>
          <CardDescription>
            Send a test message to see how the AI will respond
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="test-message">Test Message</Label>
            <Input
              id="test-message"
              placeholder="What are your hours?"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
            />
          </div>

          <Button onClick={handleTest} disabled={testing}>
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Test AI Response
          </Button>

          {testResponse && (
            <div className="p-4 bg-muted rounded-lg">
              <p className="text-sm font-medium mb-2">AI Response:</p>
              <p className="text-sm">{testResponse}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
